import { zodResolver } from '@hookform/resolvers/zod';
import { UpdateCustomerChildrenRequestSchema, type UpdateCustomerChildrenPayload } from '@weft/api-definition';
import type { ChildCustomerOverview, Customer } from '@weft/types';
import { extract, uniqueObjects } from '@weft/utils';
import { useUpdateCustomerChildren } from 'apps/app/src/hooks/requests/use-update-customer-children';
import { runMutation } from 'apps/app/src/utils/vy-mutation';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { CustomerViewPropertiesBlock } from '../CustomerViewPropertiesBlock';
import { AddCustomerChildModal } from './AddCustomerChildModal/AddCustomerChildModal';
import { CustomerInfoViewEditChildrenItem } from './CustomerInfoViewEditChildrenItem';

type CustomerInfoViewEditChildrenProps = { 
  customerChildren: Customer[];
  customerName: string;
  customerId: string;
};

export const CustomerInfoViewEditChildren: React.FC<CustomerInfoViewEditChildrenProps> = ({ customerChildren, customerId, customerName }) => {
  const [isInEditMode, setIsInEditMode] = useState(false);
  const [selectedChildren, setSelectedChildren] = useState<ChildCustomerOverview[]>(buildSelectedChildren(customerChildren, []));
  const updateCustomerChildrenMutation = useUpdateCustomerChildren(customerId);
  
  const { setValue, resetField, handleSubmit } = useForm<UpdateCustomerChildrenPayload>({
    resolver: zodResolver(UpdateCustomerChildrenRequestSchema),
    defaultValues: {
      childrenIds: extract(selectedChildren, 'id'),
    },
  });

  const onSubmit = handleSubmit(async ({ childrenIds }) => {
    await runMutation(
      updateCustomerChildrenMutation,
      { childrenIds },
      {
        pending: 'Updating entities & branches...',
        success: 'Entities & branches updated successfully',
        error: 'Failed to update entities & branches',
      });
  });

  const handleAddChild = (newCustomerChild: ChildCustomerOverview) => {
    const existingChildrenIds = extract(selectedChildren, 'id');
    setValue('childrenIds', [...existingChildrenIds, newCustomerChild.id]);
    setSelectedChildren(uniqueObjects([...selectedChildren, newCustomerChild]));
  };

  const handleDeleteChild = (deletedCustomerChild: ChildCustomerOverview) => { 
    const existingChildrenIds = extract(selectedChildren, 'id');
    const updatedChildrenIds = existingChildrenIds.filter((existingCustomerChildId) => existingCustomerChildId !== deletedCustomerChild.id);
    setValue('childrenIds', updatedChildrenIds);
    const newSelectedChildren = selectedChildren.filter((selectedChild) => selectedChild.id !== deletedCustomerChild.id);
    setSelectedChildren(newSelectedChildren);
  };

  const handleCancelClick = () => {
    resetField('childrenIds');
    setSelectedChildren(buildSelectedChildren(customerChildren, []));
  };

  return (
    <>
      <CustomerViewPropertiesBlock
        title='Entities & Branches'
        childrenContainerClassName='flex-col flex-nowrap gap-8'
        className='pb-6'
        showEditButton
        onEditChange={setIsInEditMode}
        onCancel={handleCancelClick}
        onSave={onSubmit}
      >
        {selectedChildren?.map((customerChild)=>{
          return (
            <CustomerInfoViewEditChildrenItem
              key={crypto.randomUUID()}
              customerName={customerName}
              customerChild={customerChild}
              isInEditMode={isInEditMode}
              handleDeleteChild={() => handleDeleteChild(customerChild)}
            />
          );
        })}
        <AddCustomerChildModal
          disabled={!isInEditMode}
          onChildSelect={handleAddChild}
          customerId={customerId}
          childrenToExclude={selectedChildren}
          childremToAdd={buildSelectedChildren(customerChildren, [])}
        />
      </CustomerViewPropertiesBlock>
    </>
  );
};

function buildSelectedChildren(existingChildren: Customer[], potentialChildren: ChildCustomerOverview[]): ChildCustomerOverview[] {
  const existingChildrenOverviews: ChildCustomerOverview[] = existingChildren.map((existingChild) => {
    return {
      id: existingChild.id,
      name: existingChild.name,
      externalId: existingChild.externalId,
    };
  });

  return uniqueObjects([...existingChildrenOverviews, ...potentialChildren]);
}